import { Select, TreeSelect } from 'antd'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import * as React from 'react'
import type { FilteredMetadata, Origin, Destination } from '../../../../../../api/useFreight/useFreight'

export type LocationViewType = 'State' | 'Region' | 'None'
export type LocationType = 'terminals' | 'sites'

interface ConditionalLocationSelectorProps {
  value?: string[]
  onChange?: (value: string[]) => void
  placeholder?: string
  label?: string
  locations: Array<Origin | Destination>
  locationTypeFilter: LocationType
  groupBy: LocationViewType
  loading?: boolean
  disabled?: boolean
}

function ConditionalLocationSelector({
  value = [],
  onChange,
  placeholder = 'Select locations',
  label,
  locations = [],
  locationTypeFilter,
  groupBy,
  loading,
  disabled,
}: ConditionalLocationSelectorProps) {
  const filteredLocations = React.useMemo(() => {
    return locations.filter((loc) => {
      if (locationTypeFilter === 'terminals') {
        return loc.type === 'Terminal'
      } else {
        return loc.type === 'Store'
      }
    })
  }, [locations, locationTypeFilter])

  const treeData = React.useMemo(() => {
    if (groupBy === 'None') {
      return filteredLocations
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((loc) => ({
          title: (
            <div style={{ padding: '4px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Texto weight='bold'>{loc.name}</Texto>
                <Texto category='label' appearance='hint'>
                  {[loc.freight_region_name, loc.state].filter(Boolean).join(' • ')}
                </Texto>
              </div>
              {loc.address && (
                <Texto category='label' appearance='hint'>
                  {loc.address}
                </Texto>
              )}
            </div>
          ),
          label: loc.name,
          value: loc.id,
          key: loc.id,
        }))
    }

    const grouped = new Map<string, Array<Origin | Destination>>()

    for (const location of filteredLocations) {
      const key = groupBy === 'State' ? location.state || 'No State' : location.freight_region_name || 'No Region'

      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key)?.push(location)
    }

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, locs]) => ({
        title: (
          <Horizontal verticalCenter gap={8}>
            <Texto weight='semibold'>{group}</Texto>
            <Texto category='label' appearance='hint'>
              ({locs.length})
            </Texto>
          </Horizontal>
        ),
        label: `${group} (${locs.length})`,
        value: `group_${group}`,
        key: `group_${group}`,
        children: locs
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((loc) => ({
            title: (
              <div style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Texto weight='bold'>{loc.name}</Texto>
                  <Texto category='label' appearance='hint'>
                    {[loc.freight_region_name, loc.state].filter(Boolean).join(' • ')}
                  </Texto>
                </div>
                {loc.address && (
                  <Texto category='label' appearance='hint'>
                    {loc.address}
                  </Texto>
                )}
              </div>
            ),
            label: loc.name,
            value: loc.id,
            key: loc.id,
          })),
      }))
  }, [filteredLocations, groupBy])

  const handleTreeSelectChange = (selectedValues: string[]) => {
    const expandedValues = new Set<string>()

    for (const value of selectedValues) {
      if (value.startsWith('group_')) {
        const groupName = value.replace('group_', '')
        const grouped = new Map<string, Array<Origin | Destination>>()

        for (const location of filteredLocations) {
          const key = groupBy === 'State' ? location.state || 'No State' : location.freight_region_name || 'No Region'

          if (!grouped.has(key)) {
            grouped.set(key, [])
          }
          grouped.get(key)?.push(location)
        }

        const groupLocations = grouped.get(groupName)
        if (groupLocations) {
          groupLocations.forEach((loc) => expandedValues.add(loc.id))
        }
      } else {
        expandedValues.add(value)
      }
    }

    onChange?.(Array.from(expandedValues))
  }

  const getInitialTreeSelectValue = () => {
    if (groupBy === 'None') {
      return value
    }

    const selectedValues = [...value]

    const grouped = new Map<string, Array<Origin | Destination>>()
    for (const location of filteredLocations) {
      const key = groupBy === 'State' ? location.state || 'No State' : location.freight_region_name || 'No Region'

      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key)?.push(location)
    }

    for (const [group, locs] of grouped.entries()) {
      const groupLocationIds = locs.map((loc) => loc.id)
      const selectedInGroup = groupLocationIds.filter((id) => value.includes(id))

      if (selectedInGroup.length === groupLocationIds.length && groupLocationIds.length > 0) {
        selectedValues.push(`group_${group}`)
        groupLocationIds.forEach((id) => {
          const index = selectedValues.indexOf(id)
          if (index > -1) {
            selectedValues.splice(index, 1)
          }
        })
      }
    }

    return selectedValues
  }

  if (groupBy === 'None') {
    return (
      <div>
        {label && (
          <Texto category='label' style={{ marginBottom: 8 }}>
            {label}
          </Texto>
        )}
        <Select
          mode='multiple'
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          loading={loading}
          disabled={disabled}
          allowClear
          showSearch
          filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false}
          style={{ width: '100%' }}
          maxTagCount='responsive'
        >
          {treeData.map((item) => (
            <Select.Option key={item.key} value={item.value} label={item.label}>
              {item.title}
            </Select.Option>
          ))}
        </Select>
      </div>
    )
  }

  return (
    <div>
      {label && (
        <Texto category='label' style={{ marginBottom: 8 }}>
          {label}
        </Texto>
      )}
      <TreeSelect
        treeData={treeData}
        placeholder={placeholder}
        multiple
        treeCheckable
        allowClear
        showSearch
        treeDefaultExpandAll
        filterTreeNode={(input, node) => node?.label?.toLowerCase().includes(input.toLowerCase()) ?? false}
        maxTagCount={5}
        maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
        showCheckedStrategy={TreeSelect.SHOW_PARENT}
        value={getInitialTreeSelectValue()}
        onChange={handleTreeSelectChange}
        style={{ width: '100%' }}
        loading={loading}
        disabled={disabled}
      />
    </div>
  )
}

export default ConditionalLocationSelector
